// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms apply under AGPL-3.0 section 7(b) — see ADDITIONAL-TERMS.md

/*
 * The interface.
 *
 * One linear flow: drop a file, watch it being read, look at what came out,
 * take the report. There are no tabs, no settings panel and no dashboard,
 * because the user is not exploring — they are about to press Send on something
 * and they want to know one thing.
 *
 * Every string in here is written for somebody who is not a developer and is
 * mildly anxious. A finding is stated as a fact and paired with the exact route
 * in Word that removes it; there is no verdict anywhere, because docxray does
 * not know who the recipient is or what was agreed, and a confident "safe to
 * send" is the worst thing it could say.
 */

import './styles/main.css';
import { EventLog, stamp, type LogEntry } from './eventlog';
import { mountGlossary } from './glossary';
import { buildReport, download, findingsCsv, headlineText, type RevealImage } from './exports';
import { cropGeometry, isUndecodable } from './images';
import type { Finding, FromWorker, Item, ScanResult } from './types';

const SETTINGS_KEY = 'docxray:settings:v1';
const SAMPLE_URL = '/samples/contract-draft.docx';

interface Settings {
  /** Which finding kinds are collapsed, so a long report stays navigable. */
  collapsed: string[];
}

function loadSettings(): Settings {
  try {
    const raw = localStorage.getItem(SETTINGS_KEY);
    if (!raw) return { collapsed: [] };
    const parsed = JSON.parse(raw) as Partial<Settings>;
    return { collapsed: Array.isArray(parsed.collapsed) ? parsed.collapsed.filter((c) => typeof c === 'string') : [] };
  } catch {
    return { collapsed: [] };
  }
}

function saveSettings(): void {
  try {
    localStorage.setItem(SETTINGS_KEY, JSON.stringify(state.settings));
  } catch {
    /* private browsing refuses; the tool works without it */
  }
}

interface State {
  result: ScanResult | null;
  busy: boolean;
  error: string | null;
  fileName: string | null;
  filter: string | null;
  reveals: RevealImage[];
  settings: Settings;
}

const state: State = {
  result: null,
  busy: false,
  error: null,
  fileName: null,
  filter: null,
  reveals: [],
  settings: loadSettings(),
};

const log = new EventLog();
const app = document.getElementById('app') as HTMLElement;

const worker = new Worker(new URL('./worker.ts', import.meta.url), { type: 'module' });

worker.onmessage = (event: MessageEvent<FromWorker>) => {
  const message = event.data;
  if (message.t === 'progress') {
    setProgress(message.label, message.total === 0 ? 0 : message.done / message.total);
    return;
  }
  if (message.t === 'error') {
    state.busy = false;
    state.error = message.message;
    clearProgress();
    log.add(`Could not read the document: ${message.reason}`, 'bad');
    render();
    return;
  }
  state.busy = false;
  state.result = message.result;
  state.filter = null;
  clearProgress();

  for (const warning of message.result.warnings) log.add(warning, 'warn');
  const fired = message.result.findings.filter((f) => f.items.length > 0);
  log.add(
    `Read ${message.result.scannedParts.length} document parts — ${fired.length} of ${message.result.findings.length} checks found something`,
    fired.length > 0 ? 'warn' : 'good',
  );
  render();
  void drawReveals();
};

worker.onerror = () => {
  state.busy = false;
  state.error = 'The reader stopped unexpectedly. Reload the page and try again.';
  clearProgress();
  render();
};

/* ── ingestion ────────────────────────────────────────────────────────────── */

async function loadFile(file: File): Promise<void> {
  if (state.busy) return;
  state.busy = true;
  state.error = null;
  state.result = null;
  state.reveals = [];
  state.fileName = file.name;
  render();

  log.add(`Opening ${file.name} (${formatBytes(file.size)}) — in this tab, not uploaded`);
  setProgress('opening the document', 0);

  try {
    const buffer = await file.arrayBuffer();
    // Transferred rather than copied: a 60 MB board pack should not exist twice.
    worker.postMessage({ t: 'scan', name: file.name, bytes: buffer }, [buffer]);
  } catch (error) {
    state.busy = false;
    state.error = `That file could not be read from disk: ${error instanceof Error ? error.message : String(error)}`;
    clearProgress();
    render();
  }
}

async function loadSample(): Promise<void> {
  try {
    log.add('Fetching the sample contract from this site');
    const response = await fetch(SAMPLE_URL);
    if (!response.ok) throw new Error(`the sample returned ${response.status}`);
    const blob = await response.blob();
    // Straight through the real ingestion path — the same function a dropped
    // file takes. A separate code path for the demo is a demo that can pass
    // while the product is broken.
    await loadFile(new File([blob], 'contract-draft.docx', { type: blob.type }));
  } catch (error) {
    state.error = `The sample could not be loaded: ${error instanceof Error ? error.message : String(error)}`;
    render();
  }
}

/* ── the crop reveal ──────────────────────────────────────────────────────── */

/**
 * Draws each cropped photograph whole, with the part the document showed
 * outlined and everything else dimmed.
 *
 * `imageOrientation: 'none'` is not a detail. The default is `from-image`,
 * which applies the EXIF rotation and hands back a bitmap whose axes no longer
 * match the pixel grid `a:srcRect` addresses — so the outline would land on the
 * wrong part of the picture, confidently and invisibly.
 */
async function drawReveals(): Promise<void> {
  const finding = state.result?.findings.find((f) => f.kind === 'cropped-images');
  if (!finding) return;

  const reveals: RevealImage[] = [];
  let index = 0;

  for (const item of finding.items) {
    index += 1;
    const image = item.image;
    if (!image || image.bytes.byteLength === 0) continue;
    if (isUndecodable(image.part)) {
      log.add(`${image.part} is in a format the browser cannot decode, so it is reported without a preview`, 'warn');
      continue;
    }

    try {
      const blob = new Blob([image.bytes as BlobPart]);
      const bitmap = await createImageBitmap(blob, { imageOrientation: 'none' });
      const geometry = cropGeometry(image, bitmap.width, bitmap.height);

      const canvas = document.createElement('canvas');
      canvas.width = bitmap.width;
      canvas.height = bitmap.height;
      const ctx = canvas.getContext('2d');
      if (!ctx) continue;

      ctx.drawImage(bitmap, 0, 0);
      if (!geometry.degenerate) {
        // Dim the whole thing, then punch the visible rectangle back to full
        // strength. The hidden region is what the reader is here to see, so it
        // stays legible rather than being blacked out.
        ctx.fillStyle = 'rgba(20, 22, 30, 0.55)';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.save();
        ctx.beginPath();
        ctx.rect(geometry.x, geometry.y, geometry.width, geometry.height);
        ctx.clip();
        ctx.drawImage(bitmap, 0, 0);
        ctx.restore();

        ctx.strokeStyle = '#f5a524';
        ctx.lineWidth = Math.max(2, Math.round(Math.min(canvas.width, canvas.height) / 180));
        ctx.setLineDash([ctx.lineWidth * 4, ctx.lineWidth * 3]);
        ctx.strokeRect(geometry.x, geometry.y, geometry.width, geometry.height);
      }
      bitmap.close();

      image.naturalWidth = canvas.width;
      image.naturalHeight = canvas.height;
      item.detail = `${item.detail} — ${geometry.degenerate ? 'the crop leaves nothing visible' : `${geometry.ratio.toFixed(2)}× more photograph is stored than was shown (${Math.round(geometry.hiddenFraction * 100)}% hidden)`}`;

      reveals.push({
        part: image.part,
        dataUrl: canvas.toDataURL('image/png'),
        caption: `Picture ${index} — ${image.part}. The dashed outline is what the document displayed; everything outside it travels with the file.`,
      });
      log.add(`Decoded ${image.part} (${canvas.width}×${canvas.height}) and drew the hidden area`, 'good');
    } catch {
      log.add(`${image.part} could not be decoded, so it is reported without a preview`, 'warn');
    }
  }

  state.reveals = reveals;
  render();
}

/* ── rendering ────────────────────────────────────────────────────────────── */

function el<K extends keyof HTMLElementTagNameMap>(tag: K, className?: string, text?: string): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

function glossaryTerm(term: string, label: string): HTMLSpanElement {
  const span = el('span', 'glossary-link', label);
  span.dataset.term = term;
  span.tabIndex = 0;
  return span;
}

let progressHost: HTMLDivElement | null = null;

function setProgress(label: string, fraction: number): void {
  if (!progressHost) return;
  progressHost.hidden = false;
  const text = progressHost.querySelector('.progress-label span:first-child') as HTMLElement | null;
  const pct = progressHost.querySelector('.progress-label span:last-child') as HTMLElement | null;
  const fill = progressHost.querySelector('.progress-fill') as HTMLElement | null;
  const clamped = Math.min(1, Math.max(0, fraction));
  if (text) text.textContent = label;
  if (pct) pct.textContent = `${Math.round(clamped * 100)}%`;
  if (fill) fill.style.width = `${clamped * 100}%`;
}

function clearProgress(): void {
  if (progressHost) progressHost.hidden = true;
}

function render(): void {
  app.replaceChildren(header(), mainContent(), footer(), logToggle());
}

function header(): HTMLElement {
  const head = el('header', 'site-header');
  const inner = el('div', 'header-inner');

  const mark = el('div', 'wordmark');
  mark.innerHTML =
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z"/><path d="M14 2v6h6"/><path d="M8 13h8"/><path d="M8 17h5"/></svg>';
  mark.append(el('span', undefined, 'docxray'));
  inner.append(mark);
  inner.append(el('span', 'header-tag', 'see what is still hiding in a Word file'));

  const actions = el('div', 'header-actions');
  const how = el('button', 'btn', 'How it works');
  how.addEventListener('click', () => openModal('How docxray works', 'tpl-how'));
  const privacy = el('button', 'btn', 'Privacy');
  privacy.addEventListener('click', () => openModal('Privacy & threat model', 'tpl-privacy'));
  const about = el('button', 'btn', 'About');
  about.addEventListener('click', () => openModal('About docxray', 'tpl-about'));
  actions.append(how, privacy, about);
  inner.append(actions);
  head.append(inner);
  return head;
}

/**
 * The footer is built ONCE and reused across renders.
 *
 * This is not a micro-optimisation. The hosted feedback widget self-mounts on
 * `DOMContentLoaded` by appending a link into `.site-footer .footer-inner`, and
 * `render()` replaces the whole app subtree — so rebuilding the footer each time
 * silently destroys that link the moment the user does anything. Caught in
 * production: the footer read "… more tools & sites · Feedback" on first paint
 * and lost the Feedback link as soon as a document was loaded.
 *
 * `replaceChildren` re-inserts the same node rather than copying it, so keeping
 * one instance keeps whatever the widget added to it.
 */
let footerNode: HTMLElement | null = null;

function footer(): HTMLElement {
  if (footerNode) return footerNode;
  const foot = el('footer', 'site-footer');
  const inner = el('div', 'footer-inner');
  inner.innerHTML =
    'Built by <a href="https://benrichardson.dev/" target="_blank" rel="noopener">benrichardson.dev</a> · <a href="https://lab.benrichardson.dev" target="_blank" rel="noopener">more tools &amp; sites</a>';
  foot.append(inner);
  footerNode = foot;
  return foot;
}

function mainContent(): HTMLElement {
  const main = el('main', 'main-content');

  const trust = el('button', 'trust');
  trust.type = 'button';
  trust.innerHTML =
    '<strong>Runs entirely in your browser.</strong> Your document is opened here and is never uploaded, never modified, and never written to disk. What that does and does not cover →';
  trust.addEventListener('click', () => openModal('Privacy & threat model', 'tpl-privacy'));
  main.append(trust);

  progressHost = el('div', 'progress');
  progressHost.hidden = true;
  const label = el('div', 'progress-label');
  label.append(el('span', undefined, 'working'), el('span', undefined, '0%'));
  const bar = el('div', 'progress-bar');
  bar.append(el('div', 'progress-fill'));
  progressHost.append(label, bar);
  main.append(progressHost);

  if (state.error) {
    const notice = el('div', 'notice is-bad');
    notice.append(el('strong', undefined, 'That file could not be read. '), document.createTextNode(state.error));
    const retry = el('button', 'btn', 'Try another document');
    retry.style.marginTop = '0.75rem';
    retry.style.display = 'block';
    retry.addEventListener('click', () => {
      state.error = null;
      state.result = null;
      render();
    });
    notice.append(retry);
    main.append(notice);
  }

  main.append(dropZone());
  if (state.result) main.append(results(state.result));
  return main;
}

function dropZone(): HTMLElement {
  const step = el('section', 'step');

  const zone = el('div', 'dropzone');
  zone.tabIndex = 0;
  zone.setAttribute('role', 'button');
  zone.setAttribute('aria-label', 'Choose a Word document to inspect');
  zone.append(el('div', 'dropzone-title', state.busy ? 'Reading…' : 'Drop a Word document here'));
  zone.append(
    el(
      'div',
      'dropzone-sub',
      '.docx only — it is opened in this tab and never uploaded. Nothing is changed and nothing is saved.',
    ),
  );

  const input = el('input');
  input.type = 'file';
  input.accept = '.docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document';
  input.hidden = true;
  input.addEventListener('change', () => {
    const file = input.files?.[0];
    if (file) void loadFile(file);
  });

  const actions = el('div', 'dropzone-actions');
  const choose = el('button', 'btn is-primary', 'Choose a document');
  choose.addEventListener('click', (event) => {
    event.stopPropagation();
    input.click();
  });
  const sample = el('button', 'btn', 'Try a sample');
  sample.addEventListener('click', (event) => {
    // Without this the click also reaches the drop zone and opens the file
    // picker on top of the sample that is already loading.
    event.stopPropagation();
    void loadSample();
  });
  actions.append(choose, sample);
  zone.append(actions, input);

  zone.addEventListener('click', () => input.click());
  zone.addEventListener('keydown', (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      input.click();
    }
  });
  zone.addEventListener('dragover', (event) => {
    event.preventDefault();
    zone.classList.add('is-over');
  });
  zone.addEventListener('dragleave', () => zone.classList.remove('is-over'));
  zone.addEventListener('drop', (event) => {
    event.preventDefault();
    zone.classList.remove('is-over');
    const file = event.dataTransfer?.files?.[0];
    if (file) void loadFile(file);
  });

  step.append(zone);
  return step;
}

function results(result: ScanResult): HTMLElement {
  const wrap = el('div');
  const fired = result.findings.filter((f) => f.items.length > 0);

  const headline = el('div', `headline${fired.length === 0 ? ' is-clean' : ''}`);
  headline.append(el('p', 'headline-lead', headlineText(result)));
  const sub = el('p', 'headline-sub');
  sub.append(
    document.createTextNode(
      `${result.fileName} · ${formatBytes(result.fileSize)} · read from ${result.scannedParts.length} document parts`,
    ),
  );
  if (result.producer.application) {
    sub.append(document.createTextNode(` · produced by ${result.producer.application}`));
  }
  headline.append(sub);

  if (fired.length > 0) {
    const tally = el('div', 'tally');
    const all = el('button', `tally-chip${state.filter === null ? ' is-on' : ''}`, 'Everything');
    all.addEventListener('click', () => {
      state.filter = null;
      render();
    });
    tally.append(all);
    for (const finding of fired) {
      const chip = el('button', `tally-chip${state.filter === finding.kind ? ' is-on' : ''}`);
      const n = el('span', 'tally-n', String(finding.items.length));
      chip.append(n, document.createTextNode(` ${finding.title.toLowerCase()}`));
      chip.addEventListener('click', () => {
        state.filter = state.filter === finding.kind ? null : finding.kind;
        render();
      });
      tally.append(chip);
    }
    headline.append(tally);
  }
  wrap.append(headline);

  wrap.append(exportRow(result));

  const shown = state.filter ? result.findings.filter((f) => f.kind === state.filter) : result.findings;
  for (const finding of shown) wrap.append(findingCard(finding));

  if (!state.filter) {
    const note = el('div', 'notice is-info');
    note.append(
      document.createTextNode('docxray reads what the file records and never changes it. '),
      document.createTextNode('Every finding above says how to remove it in Word. '),
      document.createTextNode('It does not tell you whether the document is safe to send — that depends on who is receiving it.'),
    );
    wrap.append(note);
  }

  return wrap;
}

function exportRow(result: ScanResult): HTMLElement {
  const step = el('section', 'step');
  step.append(stepHead('Take it with you', 'The report is built in this tab and contains the revealed content, so it is as sensitive as the document.'));

  const row = el('div', 'export-row');

  const report = el('button', 'btn is-primary', 'Download the report');
  report.addEventListener('click', () => {
    const html = buildReport(result, state.reveals);
    download(new Blob([html], { type: 'text/html' }), `disclosure-report-${baseName(result.fileName)}.html`);
    log.add('Wrote disclosure-report.html in this tab', 'good');
  });

  const csv = el('button', 'btn', 'Download findings.csv');
  csv.addEventListener('click', () => {
    // The BOM matters here specifically: without it Excel mojibakes the first
    // accented character it meets, and in this file that is somebody's name.
    const blob = new Blob([String.fromCharCode(0xfeff), findingsCsv(result)], { type: 'text/csv;charset=utf-8' });
    download(blob, `findings-${baseName(result.fileName)}.csv`);
    log.add('Wrote findings.csv in this tab', 'good');
  });

  row.append(report, csv);

  if (state.reveals.length > 0) {
    const pics = el('button', 'btn', `Download ${state.reveals.length} revealed photograph${state.reveals.length === 1 ? '' : 's'}`);
    pics.addEventListener('click', () => {
      // One click, one file: Safari silently suppresses every programmatic
      // download after the first in a single gesture, so a loop here would hand
      // an iPhone user one picture and no error. The report already embeds them
      // all, which is why this stays a single most-recent-first convenience.
      const first = state.reveals[0];
      download(dataUrlToBlob(first.dataUrl), `revealed-${baseName(first.part)}.png`);
      log.add(`Wrote the revealed image for ${first.part}`, 'good');
      if (state.reveals.length > 1) {
        log.add('The rest are embedded in the downloadable report — browsers block repeated downloads from one click', 'info');
      }
    });
    row.append(pics);
  }

  if (typeof navigator.share === 'function') {
    const share = el('button', 'btn', 'Share the report');
    share.addEventListener('click', () => {
      const html = buildReport(result, state.reveals);
      const file = new File([html], `disclosure-report-${baseName(result.fileName)}.html`, { type: 'text/html' });
      const data: ShareData = { files: [file], title: 'docxray disclosure report' };
      if (typeof navigator.canShare === 'function' && !navigator.canShare(data)) {
        download(new Blob([html], { type: 'text/html' }), file.name);
        return;
      }
      void navigator.share(data).catch(() => {
        /* the user dismissed the sheet */
      });
    });
    row.append(share);
  }

  step.append(row);
  return step;
}

function dataUrlToBlob(dataUrl: string): Blob {
  const [head, body] = dataUrl.split(',');
  const binary = atob(body);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes], { type: /:(.*?);/.exec(head)?.[1] ?? 'image/png' });
}

function baseName(name: string): string {
  return name.replace(/\.[^.]+$/, '').replace(/[^a-zA-Z0-9._-]+/g, '-');
}

function stepHead(title: string, note?: string): HTMLElement {
  const head = el('div', 'step-head');
  head.append(el('h2', 'step-title', title));
  if (note) head.append(el('span', 'step-note', note));
  return head;
}

const GLOSSARY_FOR: Record<string, [string, string]> = {
  'tracked-changes': ['tracked-change', 'tracked change'],
  comments: ['resolved', 'resolved'],
  'hidden-text': ['hidden-text', 'hidden text'],
  'invisible-text': ['contrast-ratio', 'contrast ratio'],
  'cropped-images': ['crop', 'crop'],
  'custom-properties': ['custom-property', 'custom property'],
  'document-variables': ['document-variable', 'document variable'],
  'editing-sessions': ['editing-session', 'revision-save ID'],
  'external-links': ['orphan', 'orphaned link'],
};

function findingCard(finding: Finding): HTMLElement {
  const card = el('div', 'finding');

  const head = el('div', 'finding-head');
  head.append(el('h3', 'finding-title', finding.title));
  if (finding.items.length > 0) {
    head.append(el('span', 'finding-count', `${finding.items.length}`));
  } else {
    const pill = el('span', 'pill is-good', finding.state === 'not-recorded' ? 'not recorded' : 'none');
    head.append(pill);
  }
  if (finding.authors && finding.authors.length > 0) {
    head.append(el('span', 'finding-part', `names: ${finding.authors.join(', ')}`));
  } else if (finding.parts && finding.parts.length > 0 && finding.items.length > 0) {
    head.append(el('span', 'finding-part', finding.parts.join(', ')));
  }
  card.append(head);

  const body = el('div', 'finding-body');

  const what = el('p', 'finding-what');
  const term = GLOSSARY_FOR[finding.kind];
  if (term) {
    what.append(glossaryTerm(term[0], term[1]), document.createTextNode(' — '));
  }
  what.append(document.createTextNode(finding.what));
  body.append(what);

  if (finding.items.length === 0) {
    const none = el('p', 'finding-what');
    none.append(el('strong', undefined, finding.notRecorded ?? finding.absent));
    body.append(none);
    card.append(body);
    return card;
  }

  const collapsed = state.settings.collapsed.includes(finding.kind);
  const list = el('div');
  const LIMIT = 12;
  const items = collapsed ? [] : finding.items.slice(0, LIMIT);
  for (const item of items) list.append(itemNode(item, finding));
  body.append(list);

  if (finding.items.length > LIMIT || collapsed) {
    const more = el(
      'button',
      'btn',
      collapsed ? `Show ${finding.items.length}` : `Showing ${LIMIT} of ${finding.items.length} — show all`,
    );
    more.addEventListener('click', () => {
      if (collapsed) {
        state.settings.collapsed = state.settings.collapsed.filter((k) => k !== finding.kind);
      } else {
        for (const item of finding.items.slice(LIMIT)) list.append(itemNode(item, finding));
        more.remove();
        return;
      }
      saveSettings();
      render();
    });
    body.append(more);
  }

  if (finding.kind === 'cropped-images' && state.reveals.length > 0) {
    const grid = el('div', 'reveal-grid');
    for (const reveal of state.reveals) {
      const figure = el('div', 'reveal');
      const img = el('img');
      img.src = reveal.dataUrl;
      img.alt = reveal.caption;
      // Deliberately NOT `loading="lazy"`. These are data URLs that are already
      // fully in memory, so deferring them saves no network at all — and it
      // makes the decode depend on an intersection observation that does not
      // fire in a background tab, which leaves the headline finding rendering
      // as four empty boxes. Measured: complete=false indefinitely.
      figure.append(img);
      const cap = el('div', 'reveal-cap');
      cap.append(el('span', undefined, reveal.part));
      cap.append(el('strong', undefined, 'inside the dashes is all you saw'));
      figure.append(cap);
      grid.append(figure);
    }
    body.append(grid);
  }

  const fix = el('div', 'finding-fix');
  fix.append(el('strong', undefined, 'To remove it: '), document.createTextNode(finding.fix));
  body.append(fix);

  card.append(body);
  return card;
}

function itemNode(item: Item, finding: Finding): HTMLElement {
  const node = el('div', 'item');

  const meta = el('div', 'item-meta');
  if (item.author) meta.append(el('span', 'item-author', item.author));
  if (item.date) meta.append(el('span', undefined, item.date));
  if (item.detail) meta.append(el('span', undefined, item.detail));
  if (item.variant && finding.kind === 'comments') {
    const pill = el(
      'span',
      `pill ${item.variant === 'resolved' ? 'is-warn' : item.variant === 'unknown' ? '' : 'is-good'}`,
      item.variant === 'resolved' ? 'marked resolved — still in the file' : item.variant === 'unknown' ? 'state not recorded' : 'open',
    );
    meta.append(pill);
  }
  if (item.part) meta.append(el('span', undefined, item.part));
  node.append(meta);

  if (item.text) {
    const cls =
      item.variant === 'insert'
        ? 'item-text is-ins'
        : item.variant === 'delete' || item.variant === 'move-from'
          ? 'item-text is-del'
          : 'item-text';
    node.append(el('pre', cls, item.text));
  }

  for (const reply of item.replies ?? []) {
    const wrap = el('div', 'item-reply');
    wrap.append(itemNode(reply, finding));
    node.append(wrap);
  }

  return node;
}

/* ── overlays ─────────────────────────────────────────────────────────────── */

/**
 * Mounts an overlay with all four exits, which are not alternatives.
 *
 * `pointerdown` rather than `click`: it covers mouse, touch and pen in one
 * handler and fires where a synthetic click does not. Attaching both `click`
 * and `touchstart` to the same control double-fires on a real tap — the overlay
 * closes and instantly reopens, which the user experiences as a dead button and
 * which a desktop `.click()` in testing never reproduces.
 */
function mountOverlay(node: HTMLElement, panel: HTMLElement, onClose: () => void): () => void {
  const close = () => {
    cleanup();
    onClose();
  };
  const onPointer = (event: PointerEvent) => {
    if (!panel.contains(event.target as Node)) close();
  };
  const onKey = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      event.stopPropagation();
      close();
    }
  };
  const cleanup = () => {
    node.removeEventListener('pointerdown', onPointer);
    document.removeEventListener('keydown', onKey, true);
    node.remove();
  };
  node.addEventListener('pointerdown', onPointer);
  document.addEventListener('keydown', onKey, true);
  return close;
}

function openModal(title: string, templateId: string): void {
  const template = document.getElementById(templateId) as HTMLTemplateElement | null;
  const backdrop = el('div', 'modal-backdrop');
  const modal = el('div', 'modal');
  modal.setAttribute('role', 'dialog');
  modal.setAttribute('aria-modal', 'true');
  modal.setAttribute('aria-label', title);

  const head = el('div', 'modal-head');
  head.append(el('h2', 'modal-title', title));
  const close = el('button', 'close-x', '✕');
  close.setAttribute('aria-label', 'Close');
  close.style.marginLeft = 'auto';
  head.append(close);

  const body = el('div', 'modal-body');
  if (template) body.append(template.content.cloneNode(true));

  modal.append(head, body);
  backdrop.append(modal);
  document.body.append(backdrop);

  const dismiss = mountOverlay(backdrop, modal, () => {});
  close.addEventListener('pointerdown', (event) => {
    event.stopPropagation();
    dismiss();
  });
  close.focus();
}

function logToggle(): HTMLElement {
  const button = el('button', 'btn log-toggle', 'Event log');
  button.addEventListener('click', () => openLog());
  return button;
}

function openLog(): void {
  const backdrop = el('div', 'log-backdrop');
  const drawer = el('aside', 'log-drawer');
  drawer.setAttribute('role', 'dialog');
  drawer.setAttribute('aria-label', 'Event log');

  const head = el('div', 'log-head');
  head.append(el('strong', undefined, 'Event log'));
  const close = el('button', 'close-x', '✕');
  close.setAttribute('aria-label', 'Close the event log');
  close.style.marginLeft = 'auto';
  const copy = el('button', 'btn', 'Copy');
  copy.addEventListener('click', () => void navigator.clipboard?.writeText(log.toText()));
  head.append(close, copy);

  const list = el('div', 'log-list');
  const paint = (entries: readonly LogEntry[]) => {
    list.replaceChildren();
    for (const entry of entries) {
      const line = el('div', `log-line${entry.kind === 'info' ? '' : ` is-${entry.kind}`}`);
      line.append(el('span', 'log-time', `[${stamp(entry.at)}] `), document.createTextNode(entry.message));
      list.append(line);
    }
    list.scrollTop = list.scrollHeight;
  };
  const unsubscribe = log.subscribe(paint);

  drawer.append(head, list);
  backdrop.append(drawer);
  document.body.append(backdrop);

  const dismiss = mountOverlay(backdrop, drawer, unsubscribe);
  close.addEventListener('pointerdown', (event) => {
    event.stopPropagation();
    dismiss();
  });
}

/* ── boot ─────────────────────────────────────────────────────────────────── */

mountGlossary();
render();
log.add('docxray ready — nothing has been uploaded and nothing will be');

document.addEventListener('keydown', (event) => {
  if (event.key === '?' && !(event.target instanceof HTMLInputElement)) {
    openModal('How docxray works', 'tpl-how');
  }
});

// A .docx dropped anywhere on the page, not only on the zone. Somebody in a
// hurry aims at the middle of the window.
document.addEventListener('dragover', (event) => event.preventDefault());
document.addEventListener('drop', (event) => {
  const file = event.dataTransfer?.files?.[0];
  if (!file) return;
  event.preventDefault();
  void loadFile(file);
});
